import { useState, useRef, useEffect } from 'react';
import { SliderContainer, SliderTrack, SliderRail, SliderThumb, Mark, MarkLabel, ValueLabel, PercentageDisplay } from './slider.style';
import { SliderComponentTypes } from './slider.types';

const SliderComponent = ({
  defaultValue = 0,
  value: externalValue,
  displayValue = false,
  thumb = true,
  min = 0,
  max = 100,
  step = 1,
  size = 'medium',
  marks = false,
  customMarks = null,
  disabled = false,
  valueLabelDisplay = 'auto',
  valueText = (value) => `${value}`,
  onChange,
  customColor,
}) => {
  const [value, setValue] = useState(
    externalValue !== undefined ? externalValue : Math.max(min, Math.min(max, defaultValue))
  );

  // Sync internal state with external value prop
  useEffect(() => {
    if (externalValue !== undefined && externalValue !== value) {
      setValue(externalValue);
    }
  }, [externalValue, value]);
  const [showLabel, setShowLabel] = useState(valueLabelDisplay === 'on');
  const trackRef = useRef(null);

  const calculatePercentage = (val) => {
    return ((val - min) / (max - min)) * 100;
  }

  const findClosestValidValue = (rawValue) => {
    if (step === null && customMarks) {
      const markValues = customMarks.map(mark => mark.value);
      return markValues.reduce((prev, curr) => {
        return Math.abs(curr - rawValue) < Math.abs(prev - rawValue) ? curr : prev;
      });
    }

    return Math.round(rawValue / step) * step;
  };

  const handleMouseDown = (e) => {
    if (disabled || !thumb) return;

    const updateValue = (e) => {
      const rect = trackRef.current.getBoundingClientRect();
      const percentage = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
      const rawValue = percentage * (max - min) / 100 + min;
      const newValue = Math.max(min, Math.min(max, findClosestValidValue(rawValue)));
      setValue(newValue);
      onChange?.(newValue);
    };
    updateValue(e);

    const handleMouseMove = (e) => {
      updateValue(e);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      setShowLabel(valueLabelDisplay === 'on');
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const generateMarks = () => {
    if (!marks) return null;

    const markPoints = [];
    const marksToUse = customMarks || (
      Array.from({ length: Math.floor((max - min) / step) + 1 }, (_, i) => ({
        value: min + (i * step),
        label: valueText(min + (i * step))
      }))
    );

    marksToUse.forEach(mark => {
      const markValue = typeof mark === 'object' ? mark.value : mark;
      const markLabel = typeof mark === 'object' ? mark.label : valueText(mark);

      markPoints.push(
        <>
          <Mark
            key={`mark-${markValue}`}
            percentage={calculatePercentage(markValue)}
            $active={markValue <= value}
            customColor={customColor}
            data-testid={`slider-mark-${markValue}`} // Added data-testid here
          />
          <MarkLabel
            key={`label-${markValue}`}
            percentage={calculatePercentage(markValue)}
            data-testid={`slider-mark-label-${markValue}`} // Added data-testid here
          >
            {markLabel}
          </MarkLabel>
        </>
      );
    });

    return markPoints;
  };

  return (
    <SliderContainer data-testid="slider-component-default">
      <SliderTrack ref={trackRef} onMouseDown={handleMouseDown} data-testid="slider-track">
        <SliderRail percentage={Math.min(calculatePercentage(value), 100)} disabled={disabled} customColor={customColor} onMouseDown={handleMouseDown} data-testid="slider-rail" />
        {generateMarks()}
        <ValueLabel
          percentage={Math.min(calculatePercentage(value), 100)}
          $show={showLabel || valueLabelDisplay === 'on'}
          data-testid="slider-value-label"
        >
          {valueText(value)}
        </ValueLabel>
        {thumb && (
        <SliderThumb
          percentage={Math.min(calculatePercentage(value), 100)}
          disabled={disabled}
          onMouseEnter={() => setShowLabel(true)}
          onMouseLeave={() => setShowLabel(valueLabelDisplay === 'on')}
          customColor={customColor}
          size={size}
          data-testid="slider-thumb"
        />)}
        { displayValue && (
          <PercentageDisplay data-testid="slider-percentage-display">
            {Math.round(value)}
          </PercentageDisplay>
        )}
        { !displayValue && (
          <PercentageDisplay data-testid="slider-percentage-display">
            {Math.round(calculatePercentage(value))}%
          </PercentageDisplay>
        )}
      </SliderTrack>
    </SliderContainer>
  );
};

SliderComponent.propTypes = SliderComponentTypes;

export default SliderComponent;
